import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { env } from '@/utils/constants/env';
import pkceChallenge from 'pkce-challenge';
import { keycloakAuth, KeycloakUserInfo } from '@/utils/auth/keycloak';
import { getAllTenants, TenantResponse } from '@/api/tenant';
import { clearAllDrafts } from '@/utils/lib/draftStorage';

export type LogoutOptions = {
  // false skips the server-side revoke call; only local state is cleared.
  revoke?: boolean;
};

type Store = {
  token: string | null;
  refreshToken: string | null; // Optional field for refresh token
  authMethod: 'keycloak' | 'alurkerja_sso';
  setToken: (token: string, refreshToken: string) => void;
  // `| object` keeps `onClick={logout}` assignable: a click event is not a LogoutOptions.
  logout: (options?: LogoutOptions | object) => void;
  userProfile?: UserProfile;
  keycloakUserInfo?: KeycloakUserInfo; // For Keycloak user info
  tenants?: Tenant[];
  isSuperAdmin: boolean; // Flag to indicate if user is superadmin
  allTenants?: Tenant[]; // All tenants for superadmin
  isLoadingAllTenants?: boolean; // Loading state for all tenants
  loadUserProfile: (token: string | undefined, force: boolean) => Promise<void>;
  loadAllTenants: () => Promise<void>; // Load all tenants for superadmin
  lastProfileUpdate?: Date; // Optional field to track the last profile update time
  handleLogin: () => Promise<void>;
  getToken: (rejectedToken?: string) => Promise<string | null>;
  handleCallback: (code: string, verifier: string) => Promise<void>;
  isTokenExpired: () => boolean;
  isLogoutLoading?: boolean;
};
export interface UserProfile {
  id: number;
  name: string;
  email: string;
  public_name: null;
  avatar_url: string;
  is_admin: boolean;
  email_verified_at: Date;
  created_at: Date;
  updated_at: Date;
  tenants: Tenant[];
}

export interface Tenant {
  id: number;
  slug: string;
  name: string;
  uuid: string;
  userId: number;
  logoPath: string;
  faviconPath: string;
  roles: string[];
  subscription?: {
    id: number;
    tenant_id: number;
    status: 'active' | 'canceled' | 'expired' | 'inactive';
    trial_ends_at: string | null;
    ends_at: string | null;
  };
}

const isTokenValueExpired = (token: string, authMethod: Store['authMethod']): boolean => {
  if (authMethod === 'keycloak') {
    return keycloakAuth.isTokenExpired(token);
  }

  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) {
    console.warn('Invalid token format');
    return true;
  }

  try {
    const payload = JSON.parse(atob(tokenParts[1]));
    const exp = payload.exp;
    if (typeof exp !== 'number') {
      console.warn('Invalid exp claim in token');
      return true;
    }
    return exp * 1000 < Date.now();
  } catch (error) {
    console.error('Error decoding token:', error);
    return true;
  }
};

// Runs without a lock where the Web Locks API is unavailable.
const withRefreshLock = <T>(task: () => Promise<T>): Promise<T> =>
  typeof navigator !== 'undefined' && navigator.locks ? navigator.locks.request('auth-refresh', task) : task();

// AlurKerja errors carry `status`; keycloakAuth only encodes it in the message
// ("Token refresh failed: 400 - ...", "Failed to get user info: 401 - ..."). Network errors have neither.
const errorStatus = (error: unknown): number | undefined => {
  const status = (error as { status?: unknown })?.status;
  if (typeof status === 'number') return status;
  const match = /: (\d{3}) - /.exec(error instanceof Error ? error.message : '');
  return match ? Number(match[1]) : undefined;
};

const useAuthState = create<Store>()(
  persist(
    (set, get) => ({
      lastProfileUpdate: undefined,
      token: null,
      isLogoutLoading: false,
      refreshToken: null, // Initialize refresh token
      authMethod: env.authMethod as 'keycloak' | 'alurkerja_sso',
      keycloakUserInfo: undefined,
      tenants: [], // Initialize with empty array
      isSuperAdmin: false, // Initialize superadmin flag
      allTenants: undefined, // Initialize all tenants
      isLoadingAllTenants: false, // Initialize loading state
      setToken: (token: string, refreshToken: string) => {
        set({ token, refreshToken });
      },
      loadUserProfile: async (token, force = false) => {
        // Skip if recently updated (unless forced)
        if (!force && get().lastProfileUpdate) {
          const timeSinceUpdate = new Date().getTime() - get().lastProfileUpdate!.getTime();
          if (timeSinceUpdate < 5 * 60 * 1000) {
            return; // Skip loading if profile was updated within the last 5 minutes
          }
        }
        const initialToken = token || get().token;
        if (!initialToken) {
          console.warn('No token available for loading user profile');
          return;
        }

        const authMethod = get().authMethod;

        // A stored token may have expired while the app was closed; getToken refreshes it.
        // A token not yet in state (fresh from the login callback) is used as is.
        const activeToken = get().token ? await get().getToken() : initialToken;
        if (!activeToken) {
          // getToken already ended the session.
          throw new Error('No valid token to load user profile');
        }

        const fetchProfile = async (accessToken: string) => {
          if (authMethod === 'keycloak') {
            return keycloakAuth.getUserInfo(accessToken);
          }
          const response = await fetch(env.apiBaseUrl + '/api/me', {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          });
          if (!response.ok) {
            throw Object.assign(new Error('Failed to fetch user profile'), { status: response.status });
          }
          return response.json();
        };

        let data;
        try {
          try {
            data = await fetchProfile(activeToken);
          } catch (error) {
            if (errorStatus(error) !== 401) throw error;
            const refreshedToken = await get().getToken(activeToken);
            if (!refreshedToken) throw error;
            data = await fetchProfile(refreshedToken);
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
          // Only a rejected token ends the session; network errors and 5xx leave it intact.
          if (errorStatus(error) === 401) {
            window.location.href = `/login`;
            localStorage.removeItem('auth-storage');
          }
          throw error;
        }

        if (authMethod === 'keycloak') {
          set({ keycloakUserInfo: data, lastProfileUpdate: new Date() });
        } else {
          // Extract tenants from /me response
          const userTenants = data.tenants || [];
          const tenants = userTenants.map(
            (tenant: {
              id: number;
              uuid: string;
              name: string;
              slug: string;
              logo_url?: string;
              favicon_url?: string;
              roles?: string[];
              subscription?: {
                id: number;
                tenant_id: number;
                status: string;
                trial_ends_at: string | null;
                ends_at: string | null;
              };
            }) => ({
              id: tenant.id,
              uuid: tenant.uuid,
              name: tenant.name,
              slug: tenant.slug,
              userId: data.id,
              logoPath: tenant.logo_url || '',
              faviconPath: tenant.favicon_url || '',
              roles: tenant.roles || [],
              subscription: tenant.subscription,
            }),
          );

          const isSuperAdmin = data.is_admin === true;
          set({
            userProfile: data,
            tenants,
            isSuperAdmin,
            lastProfileUpdate: new Date(),
          });

          if (isSuperAdmin) {
            try {
              await get().loadAllTenants();
            } catch (error) {
              console.error('Failed to load all tenants for superadmin:', error);
            }
          }
        }
      },
      loadAllTenants: async () => {
        const isSuperAdmin = get().isSuperAdmin;
        if (!isSuperAdmin) {
          console.warn('User is not superadmin, cannot load all tenants');
          return;
        }

        if (get().isLoadingAllTenants) {
          return;
        }

        try {
          set({ isLoadingAllTenants: true });
          const response = await getAllTenants();
          const allTenants = response.data.tenants.map((tenant: TenantResponse) => ({
            id: tenant.id,
            uuid: tenant.uuid,
            name: tenant.name,
            slug: tenant.slug,
            userId: get().userProfile?.id || 0,
            logoPath: tenant.logo_url || '',
            faviconPath: tenant.favicon_url || '',
            roles: ['superadmin'],
          }));
          set({ allTenants, isLoadingAllTenants: false });
        } catch (error) {
          console.error('Error loading all tenants:', error);
          set({ isLoadingAllTenants: false });
        }
      },
      getToken: (rejectedToken?: string) =>
        // The lock serialises refreshes across tabs: a waiting tab rehydrates the rotated pair
        // the first tab stored instead of replaying the (now revoked) refresh token.
        withRefreshLock(async () => {
          await useAuthState.persist.rehydrate();

          const token = get().token;
          const refreshToken = get().refreshToken;
          const authMethod = get().authMethod;

          if (!token) {
            return null;
          }

          const isRejected = rejectedToken !== undefined && rejectedToken === token;

          if ((isTokenValueExpired(token, authMethod) || isRejected) && refreshToken) {
            try {
              if (authMethod === 'keycloak') {
                const tokenResponse = await keycloakAuth.refreshAccessToken(refreshToken);
                set({
                  token: tokenResponse.access_token,
                  refreshToken: tokenResponse.refresh_token,
                });
                return tokenResponse.access_token;
              } else {
                const response = await fetch(`${env.apiBaseUrl}/oauth/token`, {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    grant_type: 'refresh_token',
                    refresh_token: refreshToken,
                    client_id: env.AK_CLIENT_ID,
                  }),
                  // The refresh lock is held for the whole request.
                  signal: AbortSignal.timeout(15000),
                });

                if (!response.ok) {
                  throw Object.assign(new Error('Failed to refresh token'), { status: response.status });
                }

                const data = await response.json();
                set({ token: data.access_token, refreshToken: data.refresh_token });
                return data.access_token;
              }
            } catch (error) {
              console.error('Error refreshing token:', error);
              const status = errorStatus(error);
              // Network errors and 5xx are transient: keep the session and let the caller decide.
              if (status !== 400 && status !== 401) {
                throw error;
              }
              await useAuthState.persist.rehydrate();
              const rotatedToken = get().token;
              if (rotatedToken && rotatedToken !== token && !isTokenValueExpired(rotatedToken, authMethod)) {
                return rotatedToken;
              }
              window.location.href = `/login`;
              localStorage.removeItem('auth-storage');
              return null;
            }
          }

          return token;
        }),
      handleLogin: async () => {
        const authMethod = get().authMethod;
        const { code_verifier, code_challenge } = await pkceChallenge();
        localStorage.setItem('verifier', code_verifier);

        if (authMethod === 'keycloak') {
          const authUrl = keycloakAuth.generateAuthUrl(code_verifier, code_challenge);
          window.location.href = authUrl;
        } else {
          const params = new URLSearchParams({
            response_type: 'code',
            client_id: env.AK_CLIENT_ID,
            redirect_uri: env.AK_REDIRECT_URI,
            code_challenge: code_challenge,
            code_challenge_method: 'S256',
          });

          window.location.href = `${env.apiBaseUrl}/oauth/authorize?${params}`;
        }
      },
      logout: async (options?: LogoutOptions | object) => {
        const authMethod = get().authMethod;

        if (!get().token) {
          console.warn('No token available for logout');
          return;
        }

        set({ isLogoutLoading: true });

        try {
          if ((options as LogoutOptions | undefined)?.revoke === false) {
            // Local cleanup only: the session is already invalid, nothing to revoke server-side.
          } else if (authMethod === 'keycloak' && get().refreshToken) {
            await keycloakAuth.logout(get().refreshToken!, window.location.origin + '/login');
          } else {
            // An expired token is refreshed first so the server can still revoke it.
            const activeToken =
              (await get()
                .getToken()
                .catch(() => null)) ?? get().token;
            const response = await fetch(`${env.apiBaseUrl}/api/oauth/logout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${activeToken}`,
              },
            });

            if (!response.ok) {
              console.error('Logout failed with status:', response.status);
              // Continue with cleanup even if logout fails
            }
          }
        } catch (error) {
          console.error('Error during logout:', error);
          // Continue with cleanup even if logout fails
        }

        // Clean up local storage and state BEFORE redirect
        set({ isLogoutLoading: false, token: null, refreshToken: null });
        localStorage.removeItem('auth-storage');
        localStorage.removeItem('current-tenant-storage');
        localStorage.removeItem('processes-storage');
        // Form drafts hold company data and would otherwise stay readable on a
        // shared device after sign-out.
        clearAllDrafts();

        // Small delay to ensure state cleanup completes
        await new Promise(resolve => setTimeout(resolve, 100));

        // A user-initiated Passport logout also ends the browser's web session before returning to the SPA login.
        if (authMethod !== 'keycloak' && (options as LogoutOptions | undefined)?.revoke !== false) {
          const redirect = encodeURIComponent(`${window.location.origin}/login`);
          window.location.href = `${env.apiBaseUrl}/oauth/session-logout?redirect=${redirect}`;
          return;
        }

        // Finally redirect to login
        window.location.href = `/login`;
      },
      handleCallback: async (code: string, verifier: string) => {
        const authMethod = get().authMethod;

        try {
          if (authMethod === 'keycloak') {
            const tokenResponse = await keycloakAuth.exchangeCodeForTokens(code, verifier);
            await set({
              token: tokenResponse.access_token,
              refreshToken: tokenResponse.refresh_token,
            });
            await get().loadUserProfile(tokenResponse.access_token, true);
          } else {
            // AlurKerja SSO method (existing code)
            const response = await fetch(`${env.apiBaseUrl}/oauth/token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                grant_type: 'authorization_code',
                client_id: env.AK_CLIENT_ID,
                redirect_uri: env.AK_REDIRECT_URI,
                code,
                code_verifier: verifier,
              }),
            });

            if (!response.ok) {
              throw new Error('Failed to exchange code for token');
            }

            const data = await response.json();
            await set({ token: data.access_token, refreshToken: data.refresh_token });
            await get().loadUserProfile(data.access_token, true);
          }
        } catch (error) {
          console.error('Error during callback handling:', error);
          throw error;
        }
      },
      isTokenExpired: () => {
        const token = get().token;

        if (!token) return true;

        return isTokenValueExpired(token, get().authMethod);
      },
    }),
    {
      name: 'auth-storage', // unique name for localStorage key
      storage: createJSONStorage(() => localStorage), // use localStorage
      partialize: state => ({
        token: state.token,
        tenants: state.tenants,
        refreshToken: state.refreshToken, // persist refresh token
        authMethod: state.authMethod, // persist auth method
        keycloakUserInfo: state.keycloakUserInfo, // persist Keycloak user info
        userProfile: state.userProfile, // persist user profile
        isSuperAdmin: state.isSuperAdmin, // persist superadmin flag
        allTenants: state.allTenants, // persist all tenants for superadmin
      }), // persist token, tenants, and all state for superadmin
    },
  ),
);

if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key === 'auth-storage') {
      useAuthState.persist.rehydrate();
      // Another tab logged out: rehydrate keeps the in-memory tokens when the key is gone.
      if (event.newValue === null) {
        useAuthState.setState({ token: null, refreshToken: null });
      }
    }
  });
}

export default useAuthState;
